import { apiSlice } from "../../../features/api/apiSlice";

export type DietAssessor = {
  id: string; // assessor record id
  member_id: string;
  active: number;
  max_workload: number;
  member?: {
    id: string;
    user_id: number;
    membership_no?: string;
    surname?: string;
    firstname?: string;
    othernames?: string | null;
    email?: string;
    phone?: string;
  };
};

export type LogbookDiet = {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  is_active: number;
  status: string;
  notes?: string | null;
  approved_at?: string | null;
  created_at?: string;
  updated_at?: string;
  assessors?: DietAssessor[];
  applications?: any[];
};

export type DietSummary = {
  total_logbooks: number;
  assessed: number;
  pending: number;
  total_assessors: number;
};

export type DietAssessorStats = {
  member_id: string; // IMPORTANT: this is what assessor stats returns
  name: string;
  total_assigned_logbooks: number;
  total_assessed_logbooks: number;
  total_pending_logbooks: number;
};

export type DietLogbook = {
  id: string;
  user_id: number;
  logbook_diet_id: string;
  application_id: string;
  stage: number;
  status: string;
  total_score: string | null;
  remarks: string | null;
  supervisor_id: string | null;
  assessor_id: string | null; // NOTE: sometimes member_id, sometimes null in your payload
  assessed_by: string | null;
  created_at: string;
  updated_at: string;
};

export type DietDetailsResponse = {
  diet: LogbookDiet;
  summary: DietSummary;
  assessors: DietAssessorStats[];
  logbooks: DietLogbook[];
  applications: any[];
};

function pick(res: any) {
  return res?.data?.data ?? res?.data ?? res?.logbook ?? res;
}

export const logbookDietApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ADMIN: all diets
    getDiets: builder.query<LogbookDiet[], void>({
      query: () => "/logbook-diets",
      transformResponse: (res: any) => pick(res) || [],
      providesTags: [{ type: "LogbookDiets" as const, id: "LIST" }],
    }),

    // ADMIN: details (matches the big payload you pasted)
    getDietDetails: builder.query<DietDetailsResponse, string>({
      query: (id) => `/logbook-diets/${id}`,
      providesTags: (_res, _err, id) => [{ type: "LogbookDiets" as const, id }],
    }),

    // ✅ NEW (recommended backend): assessor pool for assignment
    // returns list of assessors to assign. Implement backend route as discussed.
    getAssessorsPool: builder.query<
      { member_id: string; name: string; email?: string }[],
      void
    >({
      query: () => "/logbook-diets/assessors",
      // transformResponse: (res: any) => pick(res) || [],
      transformResponse: (res: any) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res?.data?.data)) return res.data.data;
        return [];
      },
    }),

    // ✅ NEW (recommended backend): sync assessors assigned to diet
    // expects { assessor_ids: string[] } where ids are assessor IDs (or member_ids) based on your backend.
    syncDietAssessors: builder.mutation<
      any,
      { dietId: string; assessor_ids: string[] }
    >({
      query: ({ dietId, assessor_ids }) => ({
        url: `/logbook-diets/${dietId}/assessors`,
        method: "PUT",
        body: { assessor_ids },
      }),
      invalidatesTags: (_res, _err, { dietId }) => [
        { type: "LogbookDiets" as const, id: dietId },
      ],
    }),

    // ✅ NEW (recommended backend): admin click-through
    // uses member_id from assessor stats row
    getDietAssessorLogbooks: builder.query<
      { data: DietLogbook[]; member: any; diet: any },
      { dietId: string; memberId: string }
    >({
      query: ({ dietId, memberId }) =>
        `/logbook-diets/${dietId}/assessors/${memberId}/logbooks`,
      transformResponse: (res: any) => res ?? [],
    }),

    // ASSESSOR: my assigned diets (your backend already has this path)
    getMyDiets: builder.query<any, void>({
      query: () => "/logbook-diets/assessor/my-diets",
    }),

    // ASSESSOR: my diet logbooks
    getMyDietLogbooks: builder.query<DietLogbook[], string>({
      query: (dietId) => `/logbook-diets/assessor/my-diets/${dietId}/logbooks`,
      transformResponse: (res: any) => {
        console.log(res, "tis is the responseee");
        return pick(res) || [];
      },
    }),

    // ADMIN: create diet
    createDiet: builder.mutation<
      any,
      { title: string; start_date: string; end_date: string; notes?: string }
    >({
      query: (body) => ({
        url: "/logbook-diets",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "LogbookDiets" as const, id: "LIST" }],
    }),

    // ADMIN: delete diet
    deleteDiet: builder.mutation<any, string>({
      query: (id) => ({
        url: `/logbook-diets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "LogbookDiets" as const, id: "LIST" }],
    }),
    rejectDiet: builder.mutation<any, string>({
      query: (id) => ({
        url: `/logbook-diets/${id}/reject`,
        method: "GET",
      }),

      invalidatesTags: (_res, _err, id) => [
        { type: "LogbookDiets" as const, id },
        { type: "LogbookDiets" as const, id: "LIST" },
      ],
    }),

    // ADMIN: approve diet
    approveDiet: builder.mutation<any, string>({
      query: (id) => ({
        url: `/logbook-diets/${id}/approve`,
        method: "GET",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "LogbookDiets" as const, id },
        { type: "LogbookDiets" as const, id: "LIST" },
      ],
    }),

    // ADMIN: activate diet
    activateDiet: builder.mutation<any, string>({
      query: (id) => ({
        url: `/logbook-diets/${id}/activate`,
        method: "GET",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "LogbookDiets" as const, id },
        { type: "LogbookDiets" as const, id: "LIST" },
      ],
    }),

    // ADMIN: start assessment
    startAssessment: builder.mutation<any, string>({
      query: (id) => ({
        url: `/logbook-diets/${id}/start/assessment`,
        method: "GET",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "LogbookDiets" as const, id },
        { type: "LogbookDiets" as const, id: "LIST" },
      ],
    }),

    // ADMIN: close assessment
    closeAssessment: builder.mutation<any, string>({
      query: (id) => ({
        url: `/logbook-diets/${id}/close`,
        method: "GET",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "LogbookDiets" as const, id },
        { type: "LogbookDiets" as const, id: "LIST" },
      ],
    }),

    // ADMIN: extend diet
    extendDiet: builder.mutation<any, { id: string; new_end_date: string }>({
      query: ({ id, new_end_date }) => ({
        url: `/logbook-diets/${id}/extend`,
        method: "POST",
        body: { new_end_date },
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "LogbookDiets" as const, id },
        { type: "LogbookDiets" as const, id: "LIST" },
      ],
    }),

    unassignDietAssessorLogbook: builder.mutation<
      any,
      { dietId: string; memberId: string; logbookId: string }
    >({
      query: ({ dietId, memberId, logbookId }) => ({
        url: `/logbook-diets/${encodeURIComponent(
          dietId
        )}/assessors/${encodeURIComponent(
          memberId
        )}/logbooks/${encodeURIComponent(logbookId)}/unassign`,
        method: "POST",
      }),

      invalidatesTags: [{ type: "LogbookDiets" as const, id: "LIST" }],
    }),

    getDietUnassignedSubmittedLogbooks: builder.query<any, { dietId: string }>({
      query: ({ dietId }) =>
        `/logbook-diets/${encodeURIComponent(
          dietId
        )}/logbooks/unassigned-submitted`,
      transformResponse: (res: any) =>
        res?.data?.data ?? res?.data ?? res ?? { data: [] },
      providesTags: (_res, _err, arg) => [
        { type: "DietUnassignedLogbooks", id: arg.dietId },
      ],
    }),

    assignDietLogbooksToAssessor: builder.mutation<
      any,
      { dietId: string; assessor_member_id: string; logbook_ids: string[] }
    >({
      query: ({ dietId, ...body }) => ({
        url: `/logbook-diets/${encodeURIComponent(dietId)}/logbooks/assign`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "DietUnassignedLogbooks", id: arg.dietId },
        { type: "DietDetails", id: arg.dietId }, // if you tag your diet details
      ],
    }),

    getDietUnreviewedAcknowledApplication: builder.query<
      any,
      { dietId: string }
    >({
      query: ({ dietId }) =>
        `/logbook-diets/${encodeURIComponent(
          dietId
        )}/logbooks/unassigned-submitted`,
      transformResponse: (res: any) =>
        res?.data?.data ?? res?.data ?? res ?? { data: [] },
      providesTags: (_res, _err, arg) => [
        { type: "DietUnassignedLogbooks", id: arg.dietId },
      ],
    }),

    assignDietApplicationsToAssessor: builder.mutation<
      any,
      { dietId: string; assessor_member_id: string; logbook_ids: string[] }
    >({
      query: ({ dietId, ...body }) => ({
        url: `/logbook-diets/${encodeURIComponent(dietId)}/logbooks/assign`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "DietUnassignedLogbooks", id: arg.dietId },
        { type: "DietDetails", id: arg.dietId }, // if you tag your diet details
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDietsQuery,
  useGetDietDetailsQuery,
  useGetAssessorsPoolQuery,
  useSyncDietAssessorsMutation,
  useGetDietAssessorLogbooksQuery,
  useGetMyDietsQuery,
  useGetMyDietLogbooksQuery,

  // ✅ add these
  useCreateDietMutation,
  useDeleteDietMutation,
  useApproveDietMutation,
  useActivateDietMutation,
  useStartAssessmentMutation,
  useCloseAssessmentMutation,
  useRejectDietMutation,
  useExtendDietMutation,
  useUnassignDietAssessorLogbookMutation,

  useGetDietUnassignedSubmittedLogbooksQuery,
  useAssignDietLogbooksToAssessorMutation,

  useGetDietUnreviewedAcknowledApplicationQuery,
  useAssignDietApplicationsToAssessorMutation,
} = logbookDietApi;
